import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api, API, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Send, RotateCcw, Plus, Printer, Loader2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Eye, EyeOff, Sparkles, Trash2, X, ZoomIn, ZoomOut, QrCode, Mail, Camera, Globe } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmDelete from '../../components/ConfirmDelete';
import ScanMenuModal from '../../components/ScanMenuModal';
import { QRCodeSVG } from 'qrcode.react';

// ── Menu page structure ──
const TABS = [
  { id: 'carte', label: 'Carte' },
  { id: 'vins', label: 'Vins' },
  { id: 'boissons', label: 'Boissons' },
  { id: 'desserts', label: 'Desserts' },
];

const TAB_SECTIONS = {
  carte: ['Tapas', 'Entrees', 'Viandes', 'Poissons', 'Garnitures'],
  vins: ['Vins Blancs', 'Vins Roses', 'Vins Rouges'],
  boissons: ['Champagne', 'Aperitifs', 'Bieres', 'Spiritueux', 'Softs', 'Cafes'],
  desserts: ['Desserts'],
};

const PREVIEW_PAGES = {
  carte: [
    { title: 'APERO & TAPAS', sections: ['Tapas'], hideHeaders: true, title2: 'NOS ENTREES', sections2: ['Entrees'], hideHeaders2: true, footer: 'Menu Enfant - 16\u20ac  |  Boisson  |  Tenders  |  Deux boules de glace' },
    { title: 'NOS PLATS', sections: ['Viandes'], hideHeaders: true, subsection: 'Poissons', sections2: ['Poissons'], hideHeaders2: true, footer: 'Garnitures 3\u20ac  \u00b7  Sauces 3\u20ac\nFrites \u00b7 Riz \u00b7 Legumes \u00b7 Gratin  |  Aioli Maison \u00b7 Roquefort' },
  ],
  vins: [
    { title: 'Carte des Vins', subtitle: true, sections: ['Vins Blancs', 'Vins Roses', 'Vins Rouges'] },
  ],
  boissons: [
    { title: 'Boissons & Spiritueux', subtitle: true, sections: ['Champagne', 'Aperitifs', 'Bieres'] },
    { title: 'Spiritueux & Softs', subtitle: true, sections: ['Spiritueux', 'Softs', 'Cafes'] },
  ],
  desserts: [
    { title: 'Nos Desserts', subtitle: true, sections: ['Desserts'], hideHeaders: true },
  ],
};

const QUICK_ACTIONS = [
  { label: 'Entrees printemps', prompt: 'Propose 3 entrees de printemps avec des produits locaux catalans' },
  { label: 'Nouvelles tapas', prompt: 'Propose 3 nouvelles tapas originales inspirees de la cuisine catalane' },
  { label: 'Idees viandes', prompt: 'Propose 3 plats de viande originaux pour la carte (boeuf, porc, canard, agneau...)' },
  { label: 'Poissons saison', prompt: 'Quels poissons de saison ajouter a la carte ?' },
  { label: 'Desserts', prompt: 'Propose 2 nouveaux desserts pour la carte' },
  { label: 'Accords vins', prompt: 'Quels accords mets-vins recommandes-tu pour les plats actuels ?' },
  { label: 'Nouveau cocktail', prompt: 'Propose un cocktail signature pour Le Mas' },
];

// ── AI Chat Message Component ──
const ChatMessage = ({ msg, onAddDish }) => {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-text))] text-sm px-4 py-2.5 rounded-2xl rounded-br-md max-w-[85%]">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4">
      {msg.text && (
        <div className="bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] text-sm px-4 py-3 rounded-2xl rounded-bl-md max-w-[90%] leading-relaxed whitespace-pre-wrap">
          {msg.text}
        </div>
      )}
      {msg.suggestions?.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {msg.suggestions.map((s, i) => (
            <div key={i} className="flex items-center gap-2 bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-gold)/0.2)] rounded-xl px-3 py-2.5" data-testid={`ai-suggestion-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{s.nom}</p>
                {s.description && <p className="text-xs text-[rgb(var(--lm-text-muted))]">{s.description}</p>}
              </div>
              <span className="font-mono text-sm text-[rgb(var(--lm-gold))] shrink-0">{s.prix_ttc}&euro;</span>
              <button onClick={() => onAddDish(s)}
                className="shrink-0 text-xs bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] px-2.5 py-1 rounded-lg hover:opacity-90 transition-opacity font-medium"
                data-testid={`add-suggestion-${i}`}>
                + Ajouter
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Preview Page Component ──
const PreviewPage = ({ pageConfig, items, activePage, totalPages, showRoueQR, isLastPage, roueUrl }) => {
  const renderSection = (sectionName, sectionItems, hideHeader = false) => {
    const active = sectionItems.filter(i => i.disponible !== false);
    if (active.length === 0) return null;
    return (
      <div key={sectionName} className="mb-6">
        {!hideHeader && (
          <>
            <h3 className="text-center font-bold tracking-[0.25em] uppercase mb-1.5" style={{ color: '#1B2A52', fontFamily: 'Arial, sans-serif', fontSize: '11px' }}>
              {sectionName.toUpperCase()}
            </h3>
            <div className="w-full h-px mb-4" style={{ background: '#1B2A52' }} />
          </>
        )}
        {active.map(item => (
          <div key={item.id} className="mb-3">
            <div className="flex justify-between items-baseline gap-4">
              <span className="font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1B2A52', fontSize: '14px' }}>{item.nom}</span>
              <span className="font-bold shrink-0" style={{ fontFamily: 'Georgia, serif', color: '#1B2A52', fontSize: '14px' }}>{item.prix_ttc} &euro;</span>
            </div>
            {item.description && (
              <p className="italic mt-0.5" style={{ fontFamily: 'Georgia, serif', color: '#888', fontSize: '11px' }}>{item.description}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSubsection = (name) => (
    <p className="text-center italic my-4" style={{ fontFamily: 'Georgia, serif', color: '#1B2A52', fontSize: '13px' }}>&mdash; {name} &mdash;</p>
  );

  const renderDivider = () => (
    <p className="text-center my-5" style={{ color: '#1B2A52', letterSpacing: '0.15em', fontSize: '11px' }}>&mdash; &#9830; &mdash;</p>
  );

  const grouped = {};
  items.forEach(item => {
    const sec = item.section || 'Autre';
    if (!grouped[sec]) grouped[sec] = [];
    grouped[sec].push(item);
  });

  return (
    <div className="bg-white text-[#1B2A52] min-h-[700px] shadow-lg print-page" style={{ fontFamily: 'Georgia, serif', maxWidth: '480px', margin: '0 auto', padding: '36px 40px 24px' }}>
      {/* Logo header */}
      <div className="text-center mb-3">
        <img src="/logo-carte.jpg" alt="Le Mas" className="mx-auto" style={{ height: '100px', objectFit: 'contain' }} />
      </div>
      <div className="w-full h-px" style={{ background: '#1B2A52' }} />
      <p className="text-center italic py-2" style={{ color: '#888', fontSize: '10px', fontFamily: 'Georgia, serif' }}>Saint-Laurent-de-la-Salanque  &middot;  Pays Catalan</p>
      <div className="w-full h-px mb-5" style={{ background: '#1B2A52' }} />

      {/* Page title */}
      {pageConfig.title && (
        <>
          <div className="w-full h-px mb-1.5" style={{ background: '#1B2A52' }} />
          <h2 className="text-center font-bold tracking-[0.4em] uppercase py-2"
            style={{ color: '#1B2A52', fontFamily: 'Arial, sans-serif', fontSize: pageConfig.subtitle ? '12px' : '15px', fontStyle: pageConfig.subtitle ? 'italic' : 'normal' }}>
            {pageConfig.title}
          </h2>
          <div className="w-full h-px mb-5" style={{ background: '#1B2A52' }} />
        </>
      )}

      {/* Main sections */}
      {pageConfig.sections?.map(sec => renderSection(sec, grouped[sec] || [], pageConfig.hideHeaders))}

      {/* Divider between groups */}
      {pageConfig.title2 && (
        <>
          <div className="w-full h-px my-4" style={{ background: '#1B2A52' }} />
          <h2 className="text-center font-bold tracking-[0.4em] uppercase py-2"
            style={{ color: '#1B2A52', fontFamily: 'Arial, sans-serif', fontSize: '15px' }}>
            {pageConfig.title2}
          </h2>
          <div className="w-full h-px mb-5" style={{ background: '#1B2A52' }} />
        </>
      )}
      {pageConfig.sections2?.map(sec => {
        if (pageConfig.subsection && sec === pageConfig.sections2[0]) {
          return (
            <div key={sec}>
              {renderSubsection(pageConfig.subsection)}
              {renderSection(sec, grouped[sec] || [], pageConfig.hideHeaders2)}
            </div>
          );
        }
        return renderSection(sec, grouped[sec] || [], pageConfig.hideHeaders2);
      })}

      {/* Footer */}
      {pageConfig.footer && (
        <>
          {renderDivider()}
          <p className="text-center italic whitespace-pre-line" style={{ fontFamily: 'Georgia, serif', color: '#1B2A52', fontSize: '11px' }}>{pageConfig.footer}</p>
        </>
      )}

      {/* Roue du Mas QR */}
      {showRoueQR && isLastPage && (
        <div className="mt-6 pt-3 border-t" style={{ borderColor: '#1B2A52' }}>
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <QRCodeSVG value={roueUrl || 'https://lemas.fr/roue'} size={60} bgColor="#FFFFFF" fgColor="#1B2A52" level="L" />
            </div>
            <div className="text-left">
              <p className="font-bold tracking-wider uppercase" style={{ color: '#1B2A52', fontFamily: 'Arial, sans-serif', fontSize: '10px' }}>
                Tentez votre chance !
              </p>
              <p className="italic mt-0.5" style={{ color: '#666', fontFamily: 'Georgia, serif', fontSize: '8px' }}>
                Scannez le QR code et tournez<br />la roue pour gagner un cadeau
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Page footer */}
      <div className="mt-auto pt-8">
        <div className="w-full h-px mb-3" style={{ background: '#1B2A52' }} />
        <p className="text-center" style={{ fontFamily: 'Georgia, serif', color: '#888', fontSize: '10px' }}>www.restaurant-le-mas.fr</p>
        <p className="text-center" style={{ fontFamily: 'Georgia, serif', color: '#888', fontSize: '10px' }}>04 68 38 06 98</p>
        <p className="text-center mt-2" style={{ color: '#ccc', fontSize: '8px' }}>{activePage + 1} / {totalPages}</p>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// MAIN CARTE MANAGER
// ══════════════════════════════════════════════════════════
const CarteManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('carte');
  const [activePage, setActivePage] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `s-${Date.now()}`);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const [addingTo, setAddingTo] = useState(null);
  const [newNom, setNewNom] = useState('');
  const [newPrix, setNewPrix] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editNom, setEditNom] = useState('');
  const [editPrix, setEditPrix] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [mobilePanel, setMobilePanel] = useState('list'); // 'list' | 'ai' | 'preview'
  const [showRoueQR, setShowRoueQR] = useState(false);
  const [printZoom, setPrintZoom] = useState(100);
  const [showQrPrint, setShowQrPrint] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/carte-items');
      if (res.data.length === 0) {
        await api.post('/carte-items/import-defaults');
        const res2 = await api.get('/carte-items');
        setItems(res2.data);
      } else {
        setItems(res.data);
      }
    } catch { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);
  useEffect(() => {
    api.get('/roue/config').then(r => setShowRoueQR(r.data?.settings?.qr_on_menu === true)).catch(() => {});
  }, []);

  // Group items by section (sorted by order)
  const grouped = useMemo(() => {
    const g = {};
    const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    sorted.forEach(item => {
      const sec = item.section || 'Autre';
      if (!g[sec]) g[sec] = [];
      g[sec].push(item);
    });
    return g;
  }, [items]);

  // Current tab sections
  const currentSections = TAB_SECTIONS[activeTab] || [];
  const currentPages = PREVIEW_PAGES[activeTab] || [];
  const totalPages = Object.values(PREVIEW_PAGES).reduce((s, p) => s + p.length, 0);
  const globalPageIndex = Object.keys(PREVIEW_PAGES).reduce((s, key) => {
    if (key === activeTab) return s;
    const idx = Object.keys(PREVIEW_PAGES).indexOf(key);
    const tabIdx = Object.keys(PREVIEW_PAGES).indexOf(activeTab);
    if (idx < tabIdx) return s + PREVIEW_PAGES[key].length;
    return s;
  }, 0) + activePage;

  // Toggle item
  const toggleItem = async (id) => {
    try {
      await api.patch(`/carte-items/${id}/toggle`);
      setItems(prev => prev.map(i => i.id === id ? { ...i, disponible: !i.disponible } : i));
    } catch { toast.error('Erreur'); }
  };

  // Delete item
  const deleteItem = async (id, nom) => {
    try {
      await api.delete(`/carte-items/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success(`${nom} supprime`);
    } catch { toast.error('Erreur suppression'); }
  };

  // Reorder item (swap with neighbor)
  const moveItem = async (section, idx, direction) => {
    const sectionItems = grouped[section] || [];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sectionItems.length) return;
    const item1 = sectionItems[idx];
    const item2 = sectionItems[newIdx];
    try {
      await api.post('/carte-items/reorder', { id1: item1.id, id2: item2.id });
      // Swap locally
      setItems(prev => {
        const copy = [...prev];
        const i1 = copy.findIndex(x => x.id === item1.id);
        const i2 = copy.findIndex(x => x.id === item2.id);
        if (i1 >= 0 && i2 >= 0) {
          const tmpOrder = copy[i1].order;
          copy[i1] = { ...copy[i1], order: copy[i2].order };
          copy[i2] = { ...copy[i2], order: tmpOrder };
        }
        return copy;
      });
    } catch { toast.error('Erreur reordonnement'); }
  };

  // Open edit for item
  const startEdit = (item) => {
    setEditingItem(item.id);
    setEditNom(item.nom);
    setEditPrix(String(item.prix_ttc));
    setEditDesc(item.description || '');
  };

  // Save edit
  const saveEdit = async () => {
    if (!editNom.trim()) { toast.error('Nom requis'); return; }
    const prix = parseFloat(editPrix);
    if (!prix || prix <= 0) { toast.error('Prix requis'); return; }
    try {
      const res = await api.put(`/carte-items/${editingItem}`, {
        nom: editNom.trim(), prix_ttc: prix, description: editDesc.trim(),
      });
      setItems(prev => prev.map(i => i.id === editingItem ? { ...i, ...res.data } : i));
      toast.success('Modifie');
      setEditingItem(null);
    } catch { toast.error('Erreur'); }
  };

  // Quick add item to section
  const handleQuickAdd = async (section) => {
    const nom = newNom.trim();
    const prix = parseFloat(newPrix);
    if (!nom) { toast.error('Nom requis'); return; }
    if (!prix || prix <= 0) { toast.error('Prix requis'); return; }
    const categorie = TAB_SECTIONS.carte.includes(section) ? 'entree' :
      TAB_SECTIONS.vins.includes(section) ? 'vin' :
      TAB_SECTIONS.boissons.includes(section) ? 'boisson' : 'dessert';
    try {
      await api.post('/carte-items', { nom, prix_ttc: prix, section, categorie, description: newDesc.trim(), disponible: true });
      toast.success(`${nom} ajoute`);
      setNewNom(''); setNewPrix(''); setNewDesc(''); setAddingTo(null);
      fetchItems();
    } catch { toast.error('Erreur ajout'); }
  };

  // Send chat message
  const sendMessage = async (text) => {
    const msg = text || chatInput.trim();
    if (!msg) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const res = await api.post('/carte/ai-chat', { message: msg, session_id: sessionId });
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.response,
        suggestions: res.data.suggestions,
      }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Erreur de connexion avec l\'IA.' }]);
    }
    setChatLoading(false);
    inputRef.current?.focus();
  };

  // Add AI-suggested dish
  const addDish = async (suggestion) => {
    try {
      await api.post('/carte-items', {
        nom: suggestion.nom,
        description: suggestion.description || '',
        prix_ttc: suggestion.prix_ttc,
        section: suggestion.section,
        categorie: suggestion.categorie,
        disponible: true,
      });
      toast.success(`${suggestion.nom} ajoute a la carte`);
      fetchItems();
    } catch { toast.error('Erreur ajout'); }
  };

  // Reset chat
  const resetChat = async () => {
    setChatMessages([]);
    try { await api.post('/carte/ai-chat/reset', { session_id: sessionId }); } catch {}
  };

  // Print
  const handlePrint = () => {
    window.open(`/carte-print?tab=${activeTab}&roue=${showQrPrint}`, '_blank');
  };

  // Navigate pages
  const prevPage = () => {
    if (activePage > 0) {
      setActivePage(activePage - 1);
    } else {
      const tabs = Object.keys(PREVIEW_PAGES);
      const idx = tabs.indexOf(activeTab);
      if (idx > 0) {
        const prevTab = tabs[idx - 1];
        setActiveTab(prevTab);
        setActivePage(PREVIEW_PAGES[prevTab].length - 1);
      }
    }
  };
  const nextPage = () => {
    if (activePage < currentPages.length - 1) {
      setActivePage(activePage + 1);
    } else {
      const tabs = Object.keys(PREVIEW_PAGES);
      const idx = tabs.indexOf(activeTab);
      if (idx < tabs.length - 1) {
        setActiveTab(tabs[idx + 1]);
        setActivePage(0);
      }
    }
  };

  useEffect(() => { setActivePage(0); }, [activeTab]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  // ── Extracted panel renderers (shared between desktop & tablet) ──
  const renderLeftPanel = () => (
    <>
      <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Sections actives</p>
      {currentSections.map(section => {
        const sectionItems = grouped[section] || [];
        if (sectionItems.length === 0 && addingTo !== section) return null;
        const activeCount = sectionItems.filter(i => i.disponible !== false).length;
        return (
          <div key={section} className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <h4 className="text-xs font-medium text-[rgb(var(--lm-gold))]">{section}</h4>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{activeCount}/{sectionItems.length}</span>
                <button onClick={() => { setAddingTo(addingTo === section ? null : section); setNewNom(''); setNewPrix(''); setNewDesc(''); }}
                  className="p-0.5 rounded hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-gold))] transition-colors"
                  data-testid={`add-to-${section}`}>
                  {addingTo === section ? <X size={12} /> : <Plus size={12} />}
                </button>
              </div>
            </div>
            {addingTo === section && (
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-2.5 mb-2 space-y-1.5 animate-slide-up" data-testid={`add-form-${section}`}>
                <Input value={newNom} onChange={e => setNewNom(e.target.value)} placeholder="Nom du plat"
                  className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" autoFocus data-testid="new-item-nom" />
                <div className="flex gap-1.5">
                  <Input type="number" value={newPrix} onChange={e => setNewPrix(e.target.value)} placeholder="Prix"
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8 w-20 font-mono" data-testid="new-item-prix" />
                  <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Description"
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8 flex-1" data-testid="new-item-desc" />
                  <Button size="sm" onClick={() => handleQuickAdd(section)}
                    className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] h-8 px-2.5 text-xs" data-testid="new-item-submit">
                    <Plus size={12} />
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-0.5">
              {sectionItems.map(item => (
                editingItem === item.id ? (
                  <div key={item.id} className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-2.5 space-y-1.5 animate-slide-up" data-testid={`edit-form-${item.id}`}>
                    <Input value={editNom} onChange={e => setEditNom(e.target.value)} placeholder="Nom"
                      className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" autoFocus data-testid="edit-item-nom" />
                    <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description / details"
                      className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" data-testid="edit-item-desc" />
                    <div className="flex gap-1.5">
                      <Input type="number" step="0.5" value={editPrix} onChange={e => setEditPrix(e.target.value)} placeholder="Prix"
                        className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8 w-20 font-mono" data-testid="edit-item-prix" />
                      <div className="flex-1" />
                      <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)}
                        className="h-8 px-2 text-xs text-[rgb(var(--lm-text-muted))]" data-testid="edit-cancel">
                        <X size={12} />
                      </Button>
                      <Button size="sm" onClick={saveEdit}
                        className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] h-8 px-3 text-xs" data-testid="edit-item-save">
                        OK
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div key={item.id} className="flex items-center gap-1 py-1.5 px-1 rounded-lg hover:bg-[rgb(var(--lm-surface-elevated))] transition-colors group"
                    data-testid={`item-row-${item.id}`}>
                    <div className="flex flex-col shrink-0 -mr-0.5">
                      <button onClick={(e) => { e.stopPropagation(); moveItem(section, sectionItems.indexOf(item), -1); }}
                        disabled={sectionItems.indexOf(item) === 0}
                        className={`w-4 h-3.5 flex items-center justify-center rounded-sm transition-colors ${sectionItems.indexOf(item) === 0 ? 'text-transparent cursor-default' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]'}`}
                        data-testid={`move-up-${item.id}`}>
                        <ChevronUp size={11} strokeWidth={2.5} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveItem(section, sectionItems.indexOf(item), 1); }}
                        disabled={sectionItems.indexOf(item) === sectionItems.length - 1}
                        className={`w-4 h-3.5 flex items-center justify-center rounded-sm transition-colors ${sectionItems.indexOf(item) === sectionItems.length - 1 ? 'text-transparent cursor-default' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]'}`}
                        data-testid={`move-down-${item.id}`}>
                        <ChevronDown size={11} strokeWidth={2.5} />
                      </button>
                    </div>
                    <Switch checked={item.disponible !== false} onCheckedChange={(e) => { e?.stopPropagation?.(); toggleItem(item.id); }}
                      onClick={(e) => e.stopPropagation()}
                      className="scale-75" data-testid={`toggle-${item.id}`} />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => startEdit(item)}>
                      <span className={`text-xs block truncate ${item.disponible !== false ? 'text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-muted))] line-through'}`}>
                        {item.nom}
                      </span>
                      {item.description && (
                        <span className="text-[9px] text-[rgb(var(--lm-text-muted))] block truncate italic">{item.description}</span>
                      )}
                    </div>
                    <span className="text-[10px] font-mono text-[rgb(var(--lm-gold))] opacity-60 shrink-0">{item.prix_ttc}&euro;</span>
                    <ConfirmDelete onConfirm={() => deleteItem(item.id, item.nom)} message={`Supprimer "${item.nom}" ?`}>
                      <span className="text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-all cursor-pointer shrink-0 p-1"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`delete-item-${item.id}`}>
                        <Trash2 size={14} />
                      </span>
                    </ConfirmDelete>
                  </div>
                )
              ))}
            </div>
          </div>
        );
      })}
    </>
  );

  const renderAIPanel = () => (
    <>
      <div className="px-4 py-2.5 flex items-center gap-3 border-b border-[rgb(var(--lm-border))] shrink-0">
        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <h3 className="text-sm font-medium text-[rgb(var(--lm-text))]">Assistant Carte</h3>
        <div className="flex-1" />
        <button onClick={resetChat} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))] transition-colors" data-testid="reset-chat">
          <RotateCcw size={14} />
        </button>
      </div>
      <div className="px-3 py-2 flex gap-1.5 flex-wrap border-b border-[rgb(var(--lm-border))] shrink-0">
        {QUICK_ACTIONS.map(qa => (
          <button key={qa.label} onClick={() => sendMessage(qa.prompt)}
            className="text-[10px] px-2.5 py-1 rounded-full bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-gold)/0.15)] hover:text-[rgb(var(--lm-gold))] transition-all border border-[rgb(var(--lm-border))]"
            data-testid={`qa-${qa.label.replace(/\s/g, '-')}`}>
            {qa.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
        {chatMessages.length === 0 && (
          <div className="text-center py-12">
            <Sparkles size={28} className="mx-auto text-[rgb(var(--lm-gold)/0.25)] mb-3" />
            <p className="text-xs text-[rgb(var(--lm-text-muted))]">Demande-moi des suggestions pour ta carte...</p>
          </div>
        )}
        {chatMessages.map((msg, i) => (
          <ChatMessage key={i} msg={msg} onAddDish={addDish} />
        ))}
        {chatLoading && (
          <div className="flex items-center gap-2 text-[rgb(var(--lm-text-muted))] text-xs mb-3">
            <Loader2 size={12} className="animate-spin" /> Reflexion...
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <div className="px-3 py-2.5 border-t border-[rgb(var(--lm-border))] shrink-0">
        <div className="flex gap-2">
          <Input ref={inputRef} value={chatInput} onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ex: Propose des tapas d'ete..."
            className="bg-[rgb(var(--lm-surface-elevated))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] text-sm"
            disabled={chatLoading} data-testid="chat-input" />
          <Button onClick={() => sendMessage()} disabled={chatLoading || !chatInput.trim()} size="sm"
            className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 border-0 px-3" data-testid="chat-send">
            <Send size={14} />
          </Button>
        </div>
      </div>
    </>
  );

  const renderPreviewPanel = () => (
    <>
      <div className="flex items-center justify-between mb-2 print:hidden shrink-0">
        <button onClick={prevPage} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="prev-page">
          <ChevronLeft size={16} />
        </button>
        <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">
          Page {globalPageIndex + 1} / {totalPages}
        </span>
        <button onClick={nextPage} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="next-page">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto rounded-xl">
        {currentPages[activePage] && (
          <PreviewPage pageConfig={currentPages[activePage]} items={items} activePage={globalPageIndex} totalPages={totalPages}
            showRoueQR={showRoueQR} isLastPage={globalPageIndex === totalPages - 1}
            roueUrl={window.location.origin + '/roue'} />
        )}
      </div>
    </>
  );

  return (
    <>
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="carte-manager">
      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-2 print:hidden flex-wrap">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]'
                : 'text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))]'
            }`}
            data-testid={`tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />

        {/* Panel toggle for tablet/mobile */}
        <div className="flex xl:hidden gap-0.5 bg-[rgb(var(--lm-surface))] rounded-lg p-0.5 border border-[rgb(var(--lm-border))]" data-testid="panel-toggle">
          {[
            { id: 'list', icon: '☰', label: 'Plats' },
            { id: 'ai', icon: '✦', label: 'IA' },
            { id: 'preview', icon: '◎', label: 'Apercu' },
          ].map(p => (
            <button key={p.id} onClick={() => setMobilePanel(p.id)}
              className={`px-2.5 py-1 text-[10px] rounded-md transition-all ${
                mobilePanel === p.id
                  ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] font-medium'
                  : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'
              }`}
              data-testid={`panel-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>

        <Button onClick={handlePrint} size="sm" variant="outline" className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-secondary))] print:hidden ml-1" data-testid="print-btn">
          <Printer size={14} className="mr-1" /> <span className="hidden sm:inline">Imprimer</span>
        </Button>
        <Button onClick={() => setShowEmailModal(true)} size="sm" variant="outline" className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-secondary))] print:hidden" data-testid="email-carte-btn">
          <Mail size={14} className="mr-1" /> <span className="hidden sm:inline">Email</span>
        </Button>
        <Button onClick={() => setShowScanModal(true)} size="sm" variant="outline" className="border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-secondary))] print:hidden" data-testid="scan-carte-btn">
          <Camera size={14} className="mr-1" /> <span className="hidden sm:inline">Scan</span>
        </Button>
        <Button onClick={async () => {
          setPublishing(true);
          try {
            const r = await api.post('/carte/publish');
            toast.success(r.data.message || 'Carte en ligne !');
          } catch { toast.error('Erreur publication'); }
          setPublishing(false);
        }} disabled={publishing} size="sm" className="bg-green-600 hover:bg-green-700 text-white print:hidden" data-testid="publish-carte-btn">
          {publishing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Globe size={14} className="mr-1" />}
          <span className="hidden sm:inline">Mettre en ligne</span>
        </Button>
      </div>

      {/* Print controls strip */}
      <div className="flex items-center gap-3 mb-2 px-2 py-1.5 rounded-lg bg-[rgb(var(--lm-card))] print:hidden shrink-0">
        <div className="flex items-center gap-2">
          <ZoomOut size={12} className="text-[rgb(var(--lm-text-muted))]" />
          <input type="range" min="60" max="150" value={printZoom} onChange={e => setPrintZoom(Number(e.target.value))}
            className="w-24 h-1 accent-[rgb(var(--lm-gold))]" data-testid="carte-zoom-slider" />
          <ZoomIn size={12} className="text-[rgb(var(--lm-text-muted))]" />
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))] w-8">{printZoom}%</span>
        </div>
        <div className="w-px h-4 bg-[rgb(var(--lm-border))]" />
        <label className="flex items-center gap-1.5 cursor-pointer" data-testid="carte-qr-toggle">
          <QrCode size={12} className={showQrPrint ? 'text-[rgb(var(--lm-gold))]' : 'text-[rgb(var(--lm-text-muted))]'} />
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">QR Code</span>
          <Switch checked={showQrPrint} onCheckedChange={setShowQrPrint} className="h-4 w-7" />
        </label>
      </div>

      {/* === DESKTOP (xl+): Three-column layout === */}
      <div className="flex-1 hidden xl:grid xl:grid-cols-[260px_1fr_380px] gap-2 min-h-0 print:block">
        {/* LEFT PANEL */}
        <div className="overflow-y-auto bg-[rgb(var(--lm-surface))] rounded-xl border border-[rgb(var(--lm-border))] p-3 print:hidden" data-testid="left-panel">
          {renderLeftPanel()}
        </div>
        {/* CENTER: AI */}
        <div className="flex flex-col rounded-xl overflow-hidden border border-[rgb(var(--lm-border))] print:hidden min-h-0 bg-[rgb(var(--lm-surface))]" data-testid="ai-panel">
          {renderAIPanel()}
        </div>
        {/* RIGHT: Preview */}
        <div className="flex flex-col min-h-0 print:block" data-testid="preview-panel">
          {renderPreviewPanel()}
        </div>
      </div>

      {/* === TABLET/LAPTOP (md-xl): Two-column or single panel === */}
      <div className="flex-1 xl:hidden min-h-0 print:block">
        {/* md+: side-by-side when list+ai or list+preview */}
        <div className="h-full flex flex-col md:flex-row gap-2 min-h-0">
          {/* Left/AI on tablet */}
          {(mobilePanel === 'list') && (
            <div className="flex-1 md:max-w-[320px] overflow-y-auto bg-[rgb(var(--lm-surface))] rounded-xl border border-[rgb(var(--lm-border))] p-3 print:hidden min-h-0" data-testid="left-panel-mobile">
              {renderLeftPanel()}
            </div>
          )}
          {(mobilePanel === 'ai') && (
            <div className="flex-1 flex flex-col rounded-xl overflow-hidden border border-[rgb(var(--lm-border))] print:hidden min-h-0 bg-[rgb(var(--lm-surface))]" data-testid="ai-panel-mobile">
              {renderAIPanel()}
            </div>
          )}
          {(mobilePanel === 'preview') && (
            <div className="flex-1 flex flex-col min-h-0 print:block" data-testid="preview-panel-mobile">
              {renderPreviewPanel()}
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowEmailModal(false)}>
          <div className="lm-card w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="carte-email-modal">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-[rgb(var(--lm-gold))]" />
                <h2 className="text-sm font-medium text-[rgb(var(--lm-text))]">Envoyer la carte par email</h2>
              </div>
              <button onClick={() => setShowEmailModal(false)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1 block">Destinataire</label>
              <Input type="email" placeholder="email@exemple.com" id="carte-email-to-input"
                className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="carte-email-to" autoFocus />
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1 block">Message</label>
              <textarea id="carte-email-msg-input" rows={4} defaultValue={"Bonjour,\n\nVeuillez trouver ci-joint notre carte.\n\nCordialement,"}
                className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-3 py-2 text-xs text-[rgb(var(--lm-text))] resize-none focus:outline-none focus:ring-1 focus:ring-[rgb(var(--lm-gold))]"
                data-testid="carte-email-message" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" id="carte-mgr-attach-pdf" defaultChecked={true} className="accent-[rgb(var(--lm-gold))]" data-testid="carte-mgr-attach-pdf" />
              <span className="text-xs font-medium text-[rgb(var(--lm-text))]">Joindre la carte en PDF</span>
            </label>
            <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-gold)/0.06)] border border-[rgb(var(--lm-gold)/0.15)]">
              <p className="text-[10px] text-[rgb(var(--lm-gold))]">Votre signature sera ajoutee automatiquement.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowEmailModal(false)} className="flex-1 text-xs h-9">Annuler</Button>
              <Button onClick={async () => {
                const to = document.getElementById('carte-email-to-input')?.value;
                const msg = document.getElementById('carte-email-msg-input')?.value;
                const attachPdf = document.getElementById('carte-mgr-attach-pdf')?.checked;
                if (!to) { toast.error('Email requis'); return; }
                try {
                  let res;
                  if (attachPdf) {
                    res = await api.post('/crm/send-carte-menu', {
                      to, subject: 'Carte du Restaurant - Le Mas', body: msg, carte: true, menu: false, sender: 'restaurant'
                    });
                  } else {
                    res = await api.post('/menu-du-jour/send-email', {
                      to, subject: 'Carte du Restaurant - Le Mas', message: msg,
                      menu_html: '<p>Voir notre carte : <a href="' + window.location.origin + '/site/restaurant/menu">Cliquez ici</a></p>'
                    });
                  }
                  if (res.data.success || res.data.message) { toast.success('Email envoye !'); setShowEmailModal(false); }
                  else toast.error(res.data.error || res.data.detail || 'Erreur envoi');
                } catch { toast.error('Erreur envoi'); }
              }} className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-9" data-testid="carte-send-email-btn">
                <Mail size={13} className="mr-1.5" /> Envoyer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Menu Modal */}
      {showScanModal && <ScanMenuModal onClose={() => setShowScanModal(false)} onImport={async (plats) => {
        for (const plat of plats) {
          try {
            await api.post('/carte-items', {
              nom: plat.nom,
              description: plat.description || '',
              prix_ttc: parseFloat(plat.prix_vente) || 0,
              section: plat.categorie === 'entree' ? 'Entrees' : plat.categorie === 'tapas' ? 'Tapas' : plat.categorie === 'dessert' ? 'Desserts' : plat.categorie === 'poisson' ? 'Poissons' : 'Plats',
              categorie: plat.categorie || 'plat',
              disponible: true,
            });
          } catch {}
        }
        toast.success(`${plats.length} plats ajoutes a la carte !`);
        fetchItems();
        setShowScanModal(false);
      }} />}
    </>
  );
};

export default CarteManager;
